<template>
  <div class="appointment-view container">
    <div class="row mb-2">
      <div class="col-12 col-lg-5">
        <h1>My Sessions</h1>
      </div>
      <div v-if="!rescheduleActive" class="col-12 col-lg-7 text-right filter-section">
        <div class="left">
          <form-component>
            <div class="form-row">
              <div class="col-12 col-md-4">
                <input-text
                  id="search"
                  name="search"
                  label="search"
                  :value="search"
                  @input="search=$event;loadItems();"
                />
              </div>
              <div class="col-12 col-md-4">
                <input-datepicker
                  id="start_time_after"
                  v-model="start_time_after"
                  name="start_time_after"
                  label="Start Date"
                />
              </div>
              <div class="col-12 col-md-4">
                <input-datepicker
                  id="start_time_before"
                  v-model="start_time_before"
                  name="start_time_before"
                  label="End Date"
                />
              </div>
            </div>
          </form-component>
        </div>
        <div class="right">
          <button
            type="button"
            class="btn btn-primary btn-wide pending-btn"
            @click="showPendingAppointmentRequests()"
          >
            <span>Pending</span>
            <div class="count">
              {{ appointmentRequestCount }}
            </div>
          </button>
        </div>
      </div>
      <div class="col-12">
        <hr />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div v-if="!rescheduleActive" class="dashboard-card">
          <Loader v-if="loading && appointments.length == 0" style="width: 100%;" />

          <p v-else-if="appointments.length === 0">You do not have any sessions</p>

          <appointment-table
            v-else
            :appointments="appointments"
            :edit-appointment="editAppointment"
            :sorted-asc="sortedAsc"
            :sorted-column="sortedColumn"
            :on-sort="onSort"
            @cancel="confirmCancel"
          />
          
          <Loader v-if="loading && appointments.length > 0" style="width: 100%;" />
          <div v-else-if="!pageToken" class="text-center">
            All records loaded.
          </div>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            @click="loadAppointments()"
          >
            Load More
          </button>
        </div>
        <div v-else class="dashboard-card d-md-block ">
          <div class="row align-items-center">
            <div class="col-12 col-md">
              <button class="btn btn-ghost" @click="clearScheduling">Back</button>
            </div>
            <div class="col-12 col-md-auto ml-auto pr-0 pr-md-5 pt-4 pt-md-0">
              <h3 class="text-md-right text-center">{{ selectedClient.name }}</h3>
            </div>
            <div class="col-12 col-md-2 schedule-header border rounded">
              <p class="card-subtitle mb-3"><strong>Date:</strong> {{ selectedClient.appointment.start | asMoment | format('MMMM Do, YYYY') }}</p>
              <p class="card-subtitle"><strong>Time:</strong> {{ selectedClient.appointment.start | asMoment | format('h:mm') }} - {{ selectedClient.appointment.end | asMoment | format('h:mm A') }}</p>
            </div>
          </div>
          <hr />
          <client-rescheduler :client="selectedClient" @clear-scheduling="clearScheduling" />
        </div>
      </div>
    </div>

    <pending-modal
      ref="pendingModal"
      :update-pending-requests-list="updatePendingRequestsList"
    />

    <confirm-modal
      ref="confirmModal"
      title="Cancel Session"
      confirm-body="Are you sure you want to cancel this session?"
      cancel-text="No"
      @ok="cancel"
    />
  </div>
</template>

<script>
import FormComponent from '@/components/FormComponent.vue';
import PendingModal from '@/components/modals/PendingModal.vue';
import AppointmentTable from './components/AppointmentTable.vue';
import InputDatepicker from '@/components/inputs/InputDatepicker.vue';
import InputText from '@/components/inputs/InputText.vue';
import Pagination from '@/components/Pagination.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import Loader from '@/components/Loader.vue';
import UserClient from '@/api/UserClient';
import CounselorClient from '@/api/CounselorClient';
import SessionsClient from '@/api/SessionClient';
import moment from 'moment';
import ClientRescheduler from './components/ClientRescheduler.vue';

export default {
  name: 'IndividualSessionAppointments',
  components: {
    FormComponent,
    AppointmentTable,
    Loader,
    InputDatepicker,
    InputText,
    Pagination,
    PendingModal,
    ConfirmModal,
    ClientRescheduler,
  },
  data() {
    return {
      sessionsClient: new SessionsClient(this.$http),
      counselorClient: new CounselorClient(this.$http),
      userClient: new UserClient(this.$http),

      loading: false,
      appointments: [],
      pageToken: 'ListSessionsByStartTimeDescending',
      loadItemsTimeout: null,

      // sorting
      sortedColumn: 'start_time',
      sortedAsc: false,

      search: null,
      start_time_after: '',
      start_time_before: '',

      appointmentRequests: null,
      appointmentRequestCount: 0,
      rescheduleActive: false,
      selectedClient: ''
    };
  },
  watch: {
    start_time_after() {
      this.loadItems();
    },
    start_time_before() {
      this.loadItems();
    },
  },
  async mounted() {
    await this.loadAppointments();
    this.updatePendingRequestsList();
    const appointmentToReschedule = this.$route.query.data;
    if (appointmentToReschedule) {
      let parsed = JSON.parse(appointmentToReschedule);
      parsed = { ...parsed, when : moment(parsed.when), }
      this.editAppointment(parsed);
      this.$router.replace({ path: this.$route.path, query: {} });
    }
  },
  methods: {
    showPendingAppointmentRequests() {
      this.$refs.pendingModal.show(this.appointmentRequests);
    },
    updatePendingRequestsList() {
      this.counselorClient.getMyAppointmentRequests(1000).then(res => {
        this.appointmentRequests = res.items || [];
        this.appointmentRequestCount = res.count || 0;
      }).catch(() => {
        this.appointmentRequests = [];
        this.appointmentRequestCount = 0;
      });
    },
    onPageChanged(page) {
      if (page !== this.page) {
        this.page = page;
        this.appointments = null;
        this.loadItems();
      }
    },

    onSort(sortDirection) {
      this.pageToken = sortDirection;

      this.loadItems();
    },

    loadItems() {
      if (this.loadItemsTimeout) {
        clearTimeout(this.loadItemsTimeout);
        this.loadItemsTimeout = null;
      }

      this.loadItemsTimeout = setTimeout(() => {
        this.pageToken = 'ListSessionsByStartTimeDescending';
        this.appointments = [];
        this.loadAppointments();
      }, 300);
    },

    editAppointment(apt) {
      this.rescheduleActive = true;
      this.selectedClient = {
        name: `${apt.client.firstName}  ${apt.client.lastName}`,
        appointment: {
          start: moment(apt.when),
          end: moment(apt.when).add(50, 'minutes'),
          cronofyEventId: apt.calendarEventId,
          id: apt.id
        }
      }
    },
    clearScheduling() {
      this.rescheduleActive = false;
      this.selectedClient = null;
      this.loadItems();
    },
    confirmCancel(id) {
      this.$refs.confirmModal.show(id);
    },
    cancel(id) {
      this.sessionsClient.cancelSessionByCounselor(id).then(() => {
        this.$refs.confirmModal.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Session Canceled.',
        });
        this.loadAppointments();
      }).catch(() => {
        this.$refs.confirmModal.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to cancel session. Please try again!', error: true,
        });
      });
    },
    async loadAppointments() {
      const counselorId = this.$store.getters.currentUser.counselor_profile.id;

      let searches = undefined;
      if(this.search) {
        searches = this.search
            .split(/\s+/) // match all whitespace
            .filter(Boolean) // remove empty
      }

      let from = undefined;
      if (this.start_time_after) {
        from = moment(this.start_time_after).startOf('day').toISOString();
      }

      let through = undefined
      if (this.start_time_before) {
        through = moment(this.start_time_before).endOf('day').toISOString()
      }

      try {
        this.loading = true;
        const response = await this.sessionsClient.getSessionsForCounselor(
          counselorId,
          searches, 
          from,
          through, 
          20,
          this.pageToken,
          undefined);
        
        this.pageToken = response.nextPageToken;

        response.sessions.map(x => ({
          ...x,
          when: moment(x.when),
          status: this.statusToDisplay(x),
        }))
        .forEach(x => this.appointments.push(x));
      } catch(ex) {
        console.error(ex);
      } finally {
        this.loading = false;
      }
    },
    statusToDisplay(session) {
      const status = session.status;
      if(status === 'Successful')
        return { id: status, display: 'Successful' };
      if(status === 'Upcoming')
        return { id: status, display: 'Upcoming' };
      if(status === 'InProgress')
        if(moment(session.when).add(50, 'minutes') > moment())
          return { id: status, display: 'In Progress' };
        else
          return { id: status, display: 'Pending Outcome' };

      if(status === 'UserCanceled')
        return { id: status, display: 'Canceled - User' };
      if(status === 'UserLateCanceled')
        return { id: status, display: 'Canceled - User (Late)' };
      if(status === 'AdminCanceled')
        return { id: status, display: 'Canceled - Admin' };
      if(status === 'CounselorCanceled')
        return { id: status, display: 'Canceled - Counselor' };

      if(status === 'CounselorNoShow')
        return { id: status, display: 'No Show - Counselor' };
      if(status === 'UserNoShow')
        return { id: status, display: 'No Show - User' };
      if(status === 'BothNoShow')
        return { id: status, display: 'No Show - Both' };
    }
  },
};
</script>

<style lang="scss" scoped>
.appointment-view {
  .pending-btn {
    white-space: nowrap;
    margin-bottom: 15px;
    width: 165px;
    height: 45px;

    @media (min-width: $md-min) {
      margin-top: 25px;
    }

    @media (max-width: $sm-max) {
      width: 100%;
      margin-top: 15px;
    }

    span,
    .count {
      display: inline-block;
      vertical-align: top;
    }

    .count {
      @include border-radius(200px);

      background: #132940;
      color: $green;
      margin-left: 10px;
      padding-left: 10px;
      padding-right: 10px;
      font-size: 15px;
      line-height: 24px;
      height: 24px;
    }
  }

  h1 {
    margin-top: 20px;

    @media (max-width: $md-max) {
      margin-top: 0;
      margin-bottom: 40px;
    }
  }

  ::v-deep .btn-group {
    margin-top: 8px;

    @media (max-width: $sm-max) {
      width: 100%;
    }

    .btn {
      @include border-radius($border-radius !important);

      background: rgba(255, 255, 255, 0.25) !important;
      color: white !important;
      margin-right: 5px;
      font-weight: normal;
      outline: 0 !important;
      box-shadow: none !important;

      @media (min-width: $xl-min) {
        min-width: 120px;
      }

      &.last {
        margin-right: 0;
      }

      &.active {
        background-color: white !important;
        color: rgba(0, 0, 0, 0.76) !important;
      }

      @media (max-width: $sm-max) {
        width: 25%;
      }
    }
  }
}

.filter-section {
  .left {
    width: calc(100% - 175px);
    display: inline-block;
    vertical-align: top;

    @media (max-width: $sm-max) {
      width: 100%;
    }
  }

  .right {
    width: 175px;
    display: inline-block;
    vertical-align: top;
    padding-left: 10px;

    @media (max-width: $sm-max) {
      width: 100%;
      padding-left: 0;
    }
  }
}

.schedule-header {
  padding: 1rem;
  background-color: $off-blue;
  text-align: center;
}
</style>
